import { Combobox } from "@base-ui/react/combobox"
import * as React from "react"

import OcticonXCircleFill16 from "~icons/octicon/x-circle-fill-16"
import * as Emoji from "../lib/emojis.ts"

export function ReactionPicker(props: ReactionPicker.Props) {
  const { menuButton, onSelect } = props

  const [open, setOpen] = React.useState(false)
  const [inputValue, setInputValue] = React.useState("")

  const filteredItems = React.useMemo(() => {
    if (inputValue === "") return Emoji.defaultEmojis
    return Emoji.search(Emoji.emojis, inputValue).slice(
      0,
      Emoji.defaultEmojis.length,
    )
  }, [inputValue])

  return (
    <Combobox.Root
      filter={null}
      inputValue={inputValue}
      items={filteredItems}
      onInputValueChange={setInputValue}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen)
        if (!nextOpen) setInputValue("")
      }}
      onValueChange={async (item) => {
        if (!item) return
        const emoji = (item as Emoji.Emoji).value
        try {
          await onSelect(emoji)
        } finally {
          setInputValue("")
          setOpen(false)
        }
      }}
      open={open}
      value={null}
    >
      <Combobox.Trigger render={menuButton} />

      <Combobox.Portal>
        <Combobox.Positioner sideOffset={5}>
          <Combobox.Popup className="z-100 w-fit rounded-xl border border-neutral-6 bg-muted outline-none">
            <div className="relative flex w-full items-center p-2 pb-0">
              <Combobox.Input
                className="flex w-full flex-1 rounded-md border border-neutral-6 bg-default py-2 ps-3 pe-6 text-neutral-13 text-sm leading-none outline-none placeholder:text-muted focus-within:border-accent focus-within:ring-1 focus-within:ring-accent dark:border-neutral-7 dark:text-neutral-12"
                placeholder="Search emoji"
              />
              <Combobox.Clear className="absolute end-4">
                <OcticonXCircleFill16 className="size-4 text-muted" />
              </Combobox.Clear>
            </div>

            <Combobox.List className="p-2 outline-none empty:hidden">
              {(item: Emoji.Emoji) => (
                <Combobox.Item
                  className="flex w-full cursor-default items-center gap-2 rounded-md p-2 outline-none hover:bg-control-transparent-hover data-[highlighted]:bg-control-transparent-selected"
                  key={item.id}
                  value={item}
                >
                  <div className="text-[18px] leading-none">{item.value}</div>
                  <div className="flex flex-col items-start">
                    <div className="font-medium text-sm leading-none">
                      {item.label}
                    </div>
                    <div className="text-neutral-10 text-xs leading-none dark:text-neutral-9">
                      :{item.shortcodes.at(0)}:
                    </div>
                  </div>
                </Combobox.Item>
              )}
            </Combobox.List>

            <Combobox.Empty className="p-4 text-center text-muted text-sm empty:hidden">
              No results found
            </Combobox.Empty>
          </Combobox.Popup>
        </Combobox.Positioner>
      </Combobox.Portal>
    </Combobox.Root>
  )
}

declare namespace ReactionPicker {
  type Props = {
    menuButton: React.ReactElement
    onSelect: (value: string) => Promise<void>
  }
}
